import { useState, useEffect } from "react";
import { Dialog } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import ThirdPartyServices from "../../services/ThirdParty";
import fire from "../../utils/Toastr";
import { HollowDotsSpinner } from "react-epic-spinners";

export default function UpdatePayrollOfficerModal({ 
  isOpen, 
  onClose, 
  onSuccess, 
  officer,
  organizationId 
}) {
  const [formData, setFormData] = useState({
    payrollOfficerId: "",
    organizationId: organizationId || "",
    tenantId: "",
    emailAddress: "",
    phoneNumber: "",
    officerType: "Maker",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Populate form when officer data changes
  useEffect(() => {
    if (officer) {
      setFormData({
        payrollOfficerId: officer.payrollOfficerId || "",
        organizationId: officer.organizationId || organizationId || "",
        tenantId: officer.tenantId || "",
        emailAddress: officer.emailAddress || "",
        phoneNumber: officer.phoneNumber || "",
        officerType: officer.officerType || "Maker",
      });
    }
  }, [officer, organizationId]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.emailAddress.trim()) {
      newErrors.emailAddress = "Email address is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailAddress)) {
      newErrors.emailAddress = "Please enter a valid email address";
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (!/^\d{11}$/.test(formData.phoneNumber.trim())) {
      newErrors.phoneNumber = "Phone number must be 11 digits";
    }

    if (!formData.officerType) {
      newErrors.officerType = "Officer type is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      fire("error", "Please fix the validation errors");
      return;
    }

    if (!formData.payrollOfficerId || !formData.organizationId || !formData.tenantId) {
      fire("error", "Missing required officer information");
      return;
    }

    setLoading(true);
    try {
      const response = await ThirdPartyServices.updatePayrollOfficer(formData);

      if (response.status) {
        fire("success", response.message || "Payroll officer updated successfully!");
        onSuccess(formData);
        handleClose();
      } else {
        fire("error", response.message || "Failed to update payroll officer");
      }
    } catch (error) {
      console.error("Error updating payroll officer:", error);
      fire("error", "An error occurred while updating the payroll officer");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-md w-full rounded-lg bg-white p-6">
          <div className="flex justify-between items-center mb-6">
            <Dialog.Title className="text-lg font-medium text-gray-900">
              Edit Payroll Officer
            </Dialog.Title>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XIcon className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Read-only fields */}
            <div className="bg-gray-50 p-3 rounded-md mb-4">
              <p className="text-xs text-gray-500 mb-1">Officer ID</p>
              <p className="text-sm font-mono text-gray-900 break-all">
                {formData.payrollOfficerId}
              </p>
            </div>

            {/* Email Address */}
            <div>
              <label
                htmlFor="emailAddress"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Email Address *
              </label>
              <input
                type="email"
                id="emailAddress"
                name="emailAddress"
                value={formData.emailAddress}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md text-sm ${
                  errors.emailAddress ? "border-red-300" : "border-gray-300"
                } focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent`}
                placeholder="Enter email address"
                disabled={loading}
              />
              {errors.emailAddress && (
                <p className="mt-1 text-sm text-red-600">{errors.emailAddress}</p>
              )}
            </div>

            {/* Phone Number */}
            <div>
              <label
                htmlFor="phoneNumber"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Phone Number (11 digits) *
              </label>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                maxLength={11}
                className={`w-full px-3 py-2 border rounded-md text-sm ${
                  errors.phoneNumber ? "border-red-300" : "border-gray-300"
                } focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent`}
                placeholder="Enter 11-digit phone number"
                disabled={loading}
              />
              {errors.phoneNumber && (
                <p className="mt-1 text-sm text-red-600">{errors.phoneNumber}</p>
              )}
            </div>

            {/* Officer Type */}
            <div>
              <label
                htmlFor="officerType"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Officer Type *
              </label>
              <select
                id="officerType"
                name="officerType"
                value={formData.officerType}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md text-sm ${
                  errors.officerType ? "border-red-300" : "border-gray-300"
                } focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent`}
                disabled={loading}
              >
                <option value="Maker">Maker</option>
                <option value="Checker">Checker</option>
              </select>
              {errors.officerType && (
                <p className="mt-1 text-sm text-red-600">{errors.officerType}</p>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <HollowDotsSpinner
                      color="#ffffff"
                      size={20}
                      className="mr-2"
                    />
                    Updating...
                  </>
                ) : (
                  "Update Payroll Officer"
                )}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}